// Generated abstract banner artwork, used wherever an event, team or page
// has no uploaded banner. Deterministic per seed, tinted with the accent,
// and free of any official game art.
import { useId, useMemo } from 'react'

function rng(seed) {
  let h = 1779033703 ^ seed.length
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  let a = h >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hexagon(cx, cy, r) {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i + Math.PI / 6
    return `${(cx + r * Math.cos(angle)).toFixed(1)},${(cy + r * Math.sin(angle)).toFixed(1)}`
  }).join(' ')
}

function build(seed) {
  const r = rng(seed)
  const glowX = 650 + r() * 450
  const glowY = 60 + r() * 220
  const blueX = r() * 380
  const slant = 120 + r() * 120
  const stripes = Array.from({ length: 4 }, (_, i) => {
    const x = 380 + i * (110 + r() * 70) + r() * 80
    const w = 18 + r() * 70
    return { points: `${x},0 ${x + w},0 ${x + w - slant},400 ${x - slant},400`, opacity: 0.06 + r() * 0.16 }
  })
  const hexes = Array.from({ length: 3 }, () => ({
    cx: 520 + r() * 640,
    cy: 40 + r() * 320,
    radius: 40 + r() * 130,
    opacity: 0.18 + r() * 0.25,
  }))
  return { glowX, glowY, blueX, stripes, hexes }
}

export function BannerArt({ seed = 'pred-hub', accent = '#f2800d', className }) {
  const id = useId().replace(/:/g, '')
  const art = useMemo(() => build(seed), [seed])
  return (
    <svg viewBox="0 0 1200 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0d1426" />
          <stop offset="1" stopColor="#111827" />
        </linearGradient>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0" stopColor={accent} stopOpacity="0.55" />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-blue`}>
          <stop offset="0" stopColor="#3b82f6" stopOpacity="0.35" />
          <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}-grid`} width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0V40" fill="none" stroke="#ffffff" strokeOpacity="0.045" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="1200" height="400" fill={`url(#${id}-bg)`} />
      <rect width="1200" height="400" fill={`url(#${id}-grid)`} />
      <circle cx={art.blueX} cy="40" r="380" fill={`url(#${id}-blue)`} />
      <circle cx={art.glowX} cy={art.glowY} r="460" fill={`url(#${id}-glow)`} />
      {art.stripes.map((s, i) => (
        <polygon key={i} points={s.points} fill={accent} fillOpacity={s.opacity} />
      ))}
      {art.hexes.map((h, i) => (
        <polygon
          key={i}
          points={hexagon(h.cx, h.cy, h.radius)}
          fill="none"
          stroke={accent}
          strokeOpacity={h.opacity}
          strokeWidth="2"
        />
      ))}
    </svg>
  )
}
